import { ReactNode, useState } from 'react';
import CloseIcon from '../../../assets/icons/close.svg?react';
import { cn } from '../../../utils/className';

export interface MobileSelectionAction {
  label: string;
  onClick: () => void;
  icon?: ReactNode;
  variant?: 'default' | 'primary';
}

interface MobileSelectionBarProps {
  selectedCount: number;
  actions?: MobileSelectionAction[];
  onClearSelection?: () => void;
}

/**
 * MobileSelectionBar - Fixed selection bar at the bottom of mobile screens
 * Shows selected count and available actions
 */
export function MobileSelectionBar({
  selectedCount,
  actions = [],
  onClearSelection,
}: MobileSelectionBarProps) {
  const [shouldRender, setShouldRender] = useState(false);
  const [isHoveringCount, setIsHoveringCount] = useState(false);

  const hasSelection = selectedCount > 0;
  const isVisible = hasSelection;

  // Keep component mounted if opening or if we need to animate out
  const show = hasSelection || shouldRender;

  const handleTransitionEnd = () => {
    if (!hasSelection) {
      setShouldRender(false);
      setIsHoveringCount(false);
    }
  };

  // Show immediately when opening
  if (hasSelection && !shouldRender) {
    setShouldRender(true);
  }

  if (!show) return null;

  return (
    <div
      className={cn(
        'fixed right-2 bottom-2 left-2 z-50',
        'bg-surface-primary border-border-primary rounded-lg border shadow-lg',
        'flex items-center justify-between gap-4 px-4 py-3',
        'transition-all duration-200 ease-out',
        isVisible ? 'translate-y-0 opacity-100' : 'translate-y-full opacity-0'
      )}
      onTransitionEnd={handleTransitionEnd}
    >
      {/* Selected count with clear button */}
      <div
        className={cn(
          'flex items-center gap-2',
          'transition-all duration-200',
          onClearSelection && 'cursor-pointer active:opacity-80'
        )}
        onTouchStart={() => setIsHoveringCount(true)}
        onTouchEnd={() => setIsHoveringCount(false)}
        onClick={() => {
          if (onClearSelection) {
            onClearSelection();
          }
        }}
        role={onClearSelection ? 'button' : undefined}
        aria-label={onClearSelection ? 'Clear selection' : undefined}
      >
        <div
          className='flex h-6 w-6 shrink-0 items-center justify-center
            rounded-full'
          style={{
            backgroundColor: 'var(--color-blue-100)',
            color: 'var(--color-focus)',
          }}
        >
          {isHoveringCount && onClearSelection ? (
            <CloseIcon className='h-3 w-3' />
          ) : (
            <span className='size-p2 font-regular leading-none'>
              {selectedCount}
            </span>
          )}
        </div>
        <span className='size-p2 text-primary font-regular'>Selected</span>
      </div>

      {/* Actions */}
      <div className='flex items-center gap-3'>
        {actions.map((action, index) => (
          <button
            key={index}
            onClick={action.onClick}
            className={cn(
              'flex items-center gap-1.5 rounded-lg px-3 py-1.5',
              'cursor-pointer transition-colors',
              `focus-visible:ring-focus focus-visible:ring-1
              focus-visible:outline-none`,
              '[&_path]:fill-current [&_svg]:fill-current',
              action.variant === 'primary'
                ? 'bg-focus hover:bg-focus-hover text-white'
                : 'text-primary bg-surface-secondary hover:opacity-80'
            )}
          >
            {action.icon && (
              <span
                className='flex h-4 w-4 shrink-0 items-center justify-center'
              >
                {action.icon}
              </span>
            )}
            <span className='size-p2 font-medium'>{action.label}</span>
          </button>
        ))}
      </div>
    </div>
  );
}
